import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormArray, FormsModule, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { GlobalMessageService, GlobalMessageType, UrlModule } from '@spartacus/core';
import { FormErrorsModule } from '@spartacus/storefront';
import { debounceTime, map, Subscription, tap, of } from 'rxjs';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { catchError, concatMap } from 'rxjs/operators';

@Component({
  selector: 'gen-spp-consumer-details-form',
  templateUrl: './spp-consumer-details-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    UrlModule,
    RouterModule,
    FormErrorsModule,
  ]
})
export class GenSppConsumerDetailsFormComponent implements OnInit {
  private subscriptions = new Subscription();

  @Input() sppFormValue: any;
  @Input() entryNumber: string;
  @Input() quantity: number;
  @Input() readonly: boolean;
  @Input() showEditBtn: boolean;

  sppConsumerDetailsForm = new FormGroup({
    contacts: new FormArray([])
  });

  get contacts(): FormArray {
    return this.sppConsumerDetailsForm.get('contacts') as FormArray;
  }

  cartId: string;
  userUid: string;

  isFormDirty = false;
  
  formConfigList: any[] = [];

  constructor(
    private activeCartFacade: ActiveCartFacade,
    private checkoutService: GenCheckoutService,
    private router: Router,
    private globalMessageService: GlobalMessageService,
  ) {
  }

  ngOnChanges(): void {
    if (!this.sppFormValue) return;

    this.formConfigList = Array.isArray(this.sppFormValue) ? [...this.sppFormValue] : [this.sppFormValue];

    if (this.formConfigList.length < this.quantity) {
      this.addNewFormInstance(this.quantity - this.formConfigList.length);
    }
    if (this.formConfigList.length > this.quantity) {
      this.removeFormInstance(this.formConfigList.length - this.quantity);
    }

    this.resetFormArray();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.activeCartFacade.getActive()
        .pipe(
          tap(cart => {
            this.cartId = cart?.code;
            this.userUid = cart?.user?.uid;
          })
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.contacts.valueChanges.pipe(
        debounceTime(400),
        tap(() => {
          this.isFormDirty = true;
        }),
        map(formArrayValue => {
          return this.formConfigList.map((form, index) => ({
            ...form,
            fields: form.fields.map((f: any) => ({
              ...f,
              value: formArrayValue[index]?.[f.code] ?? ''
            }))
          }));
        }),
        concatMap(updatedFormConfig =>
          this.checkoutService.saveSppFormValue(
            this.cartId,
            this.userUid,
            this.entryNumber,
            JSON.stringify(updatedFormConfig)
          ).pipe(
            catchError(error => {
              this.globalMessageService.add(
                error?.error?.errors?.[0]?.message,
                GlobalMessageType.MSG_TYPE_ERROR
              );
              return of(false);
            })
          )
        )
      ).subscribe()
    );

     this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd && event.url.indexOf('expandSppForm=true') > -1) {
          this.sppConsumerDetailsForm.markAllAsTouched();
        }
      })
    )

    if(this.router.routerState.snapshot.root.queryParams['expandSppForm'] == 'true') {
      this.sppConsumerDetailsForm.markAllAsTouched();
    }
  }

  private addNewFormInstance(count: number): void {
    const template = this.formConfigList[0];
    if (!template) return;

    for (let i = 0; i < count; i++) {
      const clonedFields = template.fields.map((f: any) => ({
        ...f,
        value: ''
      }));

      this.formConfigList.push({
        ...template,
        fields: clonedFields
      });
    }
  }

  private removeFormInstance(count: number): void {
    if (this.formConfigList.length > 0) {
      this.formConfigList.splice(-count, count);
      this.saveFormValue();
    }
  }

  private resetFormArray(): void {
    this.contacts.clear();

    this.formConfigList.forEach((form) => {
      const group = new FormGroup({});
      form.fields.forEach((f: any) => {
        group.addControl(
          f.code,
          new FormControl(f.value || '', f.required ? Validators.required : null)
        );
      });
      this.contacts.push(group);
    });
  }

  private saveFormValue(): void {
    this.subscriptions.add(
      this.activeCartFacade.getActive()
        .pipe(
          tap(cart => {
            this.cartId = cart?.code;
            this.userUid = cart?.user?.uid;

            this.checkoutService.saveSppFormValue(
              this.cartId,
              this.userUid,
              this.entryNumber,
              JSON.stringify(this.formConfigList)
            ).subscribe();
          })
        ).subscribe()
    );
  }

  getFormControl(i: number, control: string): AbstractControl {
    return this.sppConsumerDetailsForm.controls.contacts.controls[i].get(control);
  }

  ngOnDestroy(): void {
    if (this.isFormDirty) {
      this.activeCartFacade.reloadActiveCart();
    }
    
    this.subscriptions.unsubscribe();
  }
}